import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Alert,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Upload as UploadIcon,
  PersonAdd as PersonAddIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material'
import EmptyState from '../common/EmptyState'
import ErrorState from '../common/ErrorState'
import { Client, Contact, contactsApi, jobOpportunitiesApi, settingsApi } from '../../services/api'
import AddJobDialog from '../job-applications/AddJobDialog'
import { useNotification } from '../../hooks/useNotification'

import ContactFilters from '../networking/ContactFilters'
import NetworkingContactsTable from '../networking/NetworkingContactsTable'
import { normalizeCompanyName } from '../../utils/companyName'
import ContactDetailPanel from '../networking/ContactDetailPanel'
import ImportLinkedInDialog from '../networking/ImportLinkedInDialog'
import AddContactDialog from '../networking/AddContactDialog'
import AssignmentSummary from '../networking/AssignmentSummary'
import { matchesStatusFilter } from '../../constants/networking'
import {
  ASSIGNED_AWAITING_FILTER,
  ASSIGNED_AWAITING_LABEL,
  ASSIGNMENT_OVERDUE_SETTING_KEY,
  DEFAULT_ASSIGNMENT_OVERDUE_DAYS,
  isAwaitingOutreach,
  parseOverdueDays,
  summarizeAssignments,
} from '../../utils/contactAssignment'

const STAFF_EXTRA_STATUS_OPTIONS = [{ value: ASSIGNED_AWAITING_FILTER, label: ASSIGNED_AWAITING_LABEL }]

interface NetworkingTabProps {
  client: Client
}

export default function NetworkingTab({ client }: NetworkingTabProps) {
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down(768))
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const { showSuccess } = useNotification()
  const [addOpen, setAddOpen] = useState(false)
  const [referralInfo, setReferralInfo] = useState<{ contactId: string; name: string } | undefined>(undefined)
  // MWA-297 — the contact who referred the job currently being added.
  const [addJobOpen, setAddJobOpen] = useState(false)
  const [referredByForJob, setReferredByForJob] = useState<{ id: string; name: string } | null>(null)

  // Filters
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityOnly, setPriorityOnly] = useState(false)

  // MWA-354 — admin-configured days before an assigned, un-reached contact is
  // overdue. A failed read falls back to the default rather than blocking the
  // page: the list is still right, only the threshold is the stock one.
  const [overdueDays, setOverdueDays] = useState(DEFAULT_ASSIGNMENT_OVERDUE_DAYS)
  const [overdueDaysLoading, setOverdueDaysLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    settingsApi
      .get(ASSIGNMENT_OVERDUE_SETTING_KEY)
      .then((s) => {
        if (!cancelled) setOverdueDays(parseOverdueDays(s?.value))
      })
      .catch((err) => {
        console.error('Failed to load the assignment overdue threshold; using the default:', err)
      })
      .finally(() => {
        if (!cancelled) setOverdueDaysLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const fetchContacts = async () => {
    setLoading(true)
    try {
      const data = await contactsApi.list(client.id)
      setContacts(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Failed to load contacts:', err)
      setError('Failed to load contacts. The networking service may not be available yet.')
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchContacts()
  }, [client.id])

  const filteredContacts = useMemo(() => {
    let result = contacts
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(
        (c) =>
          c.fullName.toLowerCase().includes(q) ||
          (c.company || '').toLowerCase().includes(q) ||
          (c.position || '').toLowerCase().includes(q)
      )
    }
    if (statusFilter === ASSIGNED_AWAITING_FILTER) result = result.filter(isAwaitingOutreach)
    else if (statusFilter) result = result.filter((c) => matchesStatusFilter(c.status, statusFilter))
    if (priorityOnly) result = result.filter((c) => c.isPriority)
    return result
  }, [contacts, search, statusFilter, priorityOnly])

  const stats = useMemo(() => ({
    total: contacts.length,
    priority: contacts.filter((c) => c.isPriority).length,
  }), [contacts])

  // Over the whole network, not the filtered view: the accountability numbers
  // must not shrink because a search box is narrowing the table.
  const assignmentCounts = useMemo(
    () => summarizeAssignments(contacts, overdueDays, new Date()),
    [contacts, overdueDays]
  )
  const listLoading = loading || overdueDaysLoading

  // Build target company lookup from client's Dream Job data
  const targetCompanyMap = useMemo(() => {
    const map: Record<string, 'goal' | 'backup' | 'wildcard'> = {}
    const companies = client.dreamJob?.targetCompanies || []
    companies.forEach((c: unknown) => {
      if (typeof c === 'object' && c !== null && 'name' in c && 'tier' in c) {
        const tc = c as { name: string; tier: 'goal' | 'backup' | 'wildcard' }
        map[normalizeCompanyName(tc.name)] = tc.tier
      } else if (typeof c === 'string') {
        // A legacy entry carries no tier. 'wildcard' is what the server derives
        // for it, so inventing 'goal' here made one company report two tiers.
        map[normalizeCompanyName(c)] = 'wildcard'
      }
    })
    return map
  }, [client.dreamJob?.targetCompanies])

  const hasActiveFilters = !!search || !!statusFilter || priorityOnly

  const handlePriorityToggle = async (contact: Contact) => {
    try {
      const updated = await contactsApi.update({ id: contact.id, isPriority: !contact.isPriority } as Contact & { id: string })
      setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
      if (selectedContact?.id === updated.id) setSelectedContact(updated)
    } catch (err) {
      console.error('Failed to toggle priority:', err)
    }
  }

  const handleContactUpdate = (updated: Contact) => {
    setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
    setSelectedContact(updated)
  }

  const handleContactDelete = (id: string) => {
    setContacts((prev) => prev.filter((c) => c.id !== id))
  }

  const handleContactCreated = (contact: Contact) => {
    setContacts((prev) => [...prev, contact])
  }

  const handleAddReferral = (referredByContactId: string, referredByName: string) => {
    setReferralInfo({ contactId: referredByContactId, name: referredByName })
    setAddOpen(true)
  }

  // MWA-297 — the same referral, but the thing referred is a job rather than a
  // person. Captured here, on the contact, because this is where staff are
  // standing when they hear about it.
  const handleAddReferredOpportunity = (contactId: string, contactName: string) => {
    setReferredByForJob({ id: contactId, name: contactName })
    setAddJobOpen(true)
  }

  // The link rides along on the create, so there is no window in which the
  // opportunity exists without its referral recorded.
  //
  // No status: a referred job is one the client has been TOLD about, not one
  // they have applied to. Left unset it lands in Pending Client Decision, which
  // is what awaiting-the-decision means (MWA-224).
  const handleAddReferredJob = async (data: {
    clientId: string
    company: string
    jobTitle: string
  }): Promise<void> => {
    await jobOpportunitiesApi.create({
      ...data,
      awaitingDecision: true,
      linkedContactIds: referredByForJob ? [referredByForJob.id] : [],
    })
    showSuccess(
      referredByForJob
        ? `Added, with ${referredByForJob.name} recorded as the referral`
        : 'Application added'
    )
    setReferredByForJob(null)
  }

  const clientLabel = client.name || client.email || 'This client'

  if (error && contacts.length === 0) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: '10px' }}>
        <ErrorState
          title={`Couldn’t load ${clientLabel}’s contacts`}
          description="The networking service didn’t answer. Nothing has been lost — try again, and tell us if it keeps happening."
          onRetry={fetchContacts}
        />
      </Paper>
    )
  }

  if (!loading && contacts.length === 0) {
    return (
      <Box>
        <Paper variant="outlined" sx={{ borderRadius: '10px' }}>
          <EmptyState
            icon={<GroupsIcon />}
            title={`${clientLabel} has no contacts yet`}
            description="Import their LinkedIn connections, or add the people you already know about by hand."
            action={
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setImportOpen(true)} sx={{ minHeight: 44 }}>
                  Import from LinkedIn
                </Button>
                <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setAddOpen(true)} sx={{ minHeight: 44 }}>
                  Add Contact
                </Button>
              </Box>
            }
          />
        </Paper>
        <ImportLinkedInDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          clientId={client.id}
          onImportComplete={fetchContacts}
        />
        <AddContactDialog
          open={addOpen}
          onClose={() => { setAddOpen(false); setReferralInfo(undefined) }}
          clientId={client.id}
          onCreated={handleContactCreated}
          referredBy={referralInfo}
        />
      </Box>
    )
  }

  return (
    <Box>
      {/* MWA-289 — same sentence the client sees on their own network page, so
          both audiences get one description of how the list is arranged. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Contacts are organized by target company, connection type, and priority.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <AssignmentSummary
        counts={assignmentCounts}
        clientLabel={clientLabel}
        loading={listLoading}
        onShowAwaiting={() => setStatusFilter(ASSIGNED_AWAITING_FILTER)}
        awaitingFilterActive={statusFilter === ASSIGNED_AWAITING_FILTER}
      />

      {client.hasZeroTargetMatches && client.hasLinkedinImport && (client.dreamJob?.targetCompanies?.length ?? 0) > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This client has no connections at any of their target companies. Consider checking your own network or identifying hiring managers for cold outreach.
        </Alert>
      )}

      {/* Actions. The filters themselves now live in the table's own filter
          panel, so every control that narrows the list sits in one place —
          and on a phone, inside one sheet. */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setImportOpen(true)}
          sx={{ minHeight: 44, flex: { xs: '1 1 0', sm: '0 0 auto' } }}
        >
          Import More
        </Button>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setAddOpen(true)}
          sx={{ minHeight: 44, flex: { xs: '1 1 0', sm: '0 0 auto' } }}
        >
          Add Contact
        </Button>
      </Box>

      <NetworkingContactsTable
        contacts={filteredContacts}
        targetCompanyMap={targetCompanyMap}
        onContactClick={(c) => { setSelectedContact(c); setDetailOpen(true) }}
        onPriorityToggle={handlePriorityToggle}
        audience="staff"
        hasActiveFilters={hasActiveFilters}
        totalCount={stats.total}
        priorityCount={stats.priority}
        loading={listLoading}
        assignmentOverdueDays={overdueDays}
        onClearFilters={() => { setSearch(''); setStatusFilter(''); setPriorityOnly(false) }}
        filterSlot={
          <ContactFilters
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            priorityOnly={priorityOnly}
            onPriorityOnlyChange={setPriorityOnly}
            stacked={isPhone}
            extraStatusOptions={STAFF_EXTRA_STATUS_OPTIONS}
          />
        }
      />

      {/* Dialogs */}
      <ImportLinkedInDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        clientId={client.id}
        onImportComplete={fetchContacts}
      />
      <AddContactDialog
        open={addOpen}
        onClose={() => { setAddOpen(false); setReferralInfo(undefined) }}
        clientId={client.id}
        onCreated={handleContactCreated}
        referredBy={referralInfo}
      />
      <ContactDetailPanel
        contact={selectedContact}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onUpdate={handleContactUpdate}
        onDelete={handleContactDelete}
        onAddReferral={handleAddReferral}
        onAddReferredOpportunity={handleAddReferredOpportunity}
        clientTargetRole={client.dreamJob?.targetRole}
        clientName={client.name || client.email || undefined}
        audience="staff"
      />
      <AddJobDialog
        open={addJobOpen}
        onClose={() => {
          setAddJobOpen(false)
          setReferredByForJob(null)
        }}
        initialClient={client}
        referredByName={referredByForJob?.name}
        onAdd={handleAddReferredJob}
      />
    </Box>
  )
}
